import {DialogFragment, DialogFragmentProps, DialogFragmentState} from "@/shared/PageFragment";
import {BaseApp, DIALOG_FLAG_SHOW_CLOSE, DIALOG_FLAG_SHOW_DIALOG_FULLSCREEN} from "@/shared/BaseApp";

import React, {ReactElement} from "react";
import {PathComponent} from "index";
import {gray, lightGray, translucentWhite} from "@/shared/colors";
import {StyledBoxRow, StyledEditableTypography, StyledSpan} from "@/shared/StyledComponents";
import {DIVIDER, DW_SM, PD_LG, PD_MD, PD_XLG} from "@/shared/dimens";
import {Button, IconButton, Tooltip, useTheme} from "@mui/material";
import {
  AddOutlined,
  ArrowBackIosNewOutlined,
  ArrowForwardIosOutlined,
  DeleteOutlineOutlined,
  LanguageOutlined,
  LinkOutlined,
  ManageAccountsOutlined,
  MoreVertOutlined,
  OpenInNewOutlined,
  PlayArrowOutlined,
  SaveOutlined
} from "@mui/icons-material";
import {Sidebar, SidebarPosition} from "./components/Sidebar";
import {
  EDITOR_TOOLS_KTM,
  EditorTool,
  SessionText,
  Site,
  SiteDomain,
  Sites,
  WebPage
} from "@/vantage-shared/types";
import {MediaView} from "./components/MediaView";
import {Preview} from "@/vantage-shared/components/Preview";
import {Action, UserCache} from "@/shared/types";
import {getAuth} from "@firebase/auth";
import {User} from "@/shared/entities";
import {sleep} from "@/shared/sleep";
import {MediaImageUploadFragment} from "./components/MediaImageUploadFragment";
import {md5_uuid} from "@/shared/md5";
import {AccountProfileContextTrait, Page as DentwyzePage, PracticeData, SCHEDULE_DAY_KTM} from "@/libwyze-shared/types";
import {SectionView, SectionViewMode} from "@/vantage-shared/components/SectionView";
import {
  AddressSectionData,
  ContactSectionData,
  FaqItem,
  FaqSectionData,
  FooterSectionData,
  GalleryPhoto,
  GallerySectionData,
  HeaderSectionData,
  HeroSectionData,
  Link,
  OpeningHours,
  PromoSectionData,
  Service,
  ServicesDisplayMode,
  ServicesSectionData,
  ServiceType,
  SiteTemplate,
  SiteTemplatePageSections,
  Stat,
  StatsSectionData,
  TeamMember,
  TeamSectionData,
  TEMPLATES,
  Testimonial,
  TestimonialsSectionData
} from "../../../vantage-shared/page-components/types";
import {SidebarContentTemplate} from "./components/SidebarContentTemplate";
import {SidebarContentAttributes} from "./components/SidebarContentAttributes";
import {Api} from "../../../libwyze-shared/api";
import {SiteDomainsFragment} from "./SiteDomainsFragment";
import {PublishButton} from "./components/PublishButton";
import {VantageApi} from "@/vantage-shared/vantage_api";

function WebSectionContent(props: {
  webPage: WebPage,
  template: SiteTemplate,
  selectedTool: EditorTool,
  setSelectedTool: (tool: EditorTool) => void,
  sectionIndex: number,
  setSectionIndex: (sectionIndex: number) => void,
  selectedImage: string,
  setSelectedImage: (image: string) => void,
}): ReactElement {
  const theme = useTheme()
  const template = props.template;
  return <>
    <div style={{
      display: "flex",
      flexDirection: "column",
      width: 160,
      gap: 8,
      borderRight: DIVIDER,
      padding: PD_MD,
      flexShrink: 0,
      alignItems: "center",
      overflowY: "scroll"
    }}>
      {props.webPage.sections?.map((section, index) => <Button
        onClick={() => props.setSectionIndex(index)}
        style={{
          flexShrink: 0,
          padding: 0,
          width: (160 - 2 * PD_MD),
          aspectRatio: 3 / 2,
          border: props.sectionIndex === index ? "2px solid " + theme.palette.primary.main : null,
        }}>
        <SectionView
          mode={SectionViewMode.THUMBNAIL}
          webPage={props.webPage}
          template={template}
          sectionIndex={index}
          selectedSectionIndex={props.sectionIndex}
          setSelectedSectionIndex={index => {
            props.setSectionIndex(index);
            setTimeout(() => {
              document.getElementById(`section-${index}`).scrollIntoView({
                behavior: "smooth",
              });
            }, 100);
          }}
        />
      </Button>)}
    </div>
    <div style={{display: "flex", flexGrow: 1, overflow: "hidden", background: gray}}>
      <div style={{
        width: "100%",
        overflow: "scroll",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: PD_LG,
      }}>
        {props.webPage.sections?.map((_, index) => <SectionView
          id={`section-${index}`}
          mode={SectionViewMode.EDITOR}
          style={{border: DIVIDER}}
          webPage={props.webPage}
          template={template}
          sectionIndex={index}
          zoom={.84}
          selectedSectionIndex={props.sectionIndex}
          setSelectedSectionIndex={props.setSectionIndex}
          selectedImage={props.selectedImage}
          setSelectedImage={props.setSelectedImage}/>)}
      </div>
      <div style={{
        width: 48,
        borderLeft: DIVIDER,
        display: "flex",
        flexDirection: 'column',
        background: theme.palette.background.default
      }}>
        {EDITOR_TOOLS_KTM.values.map(value => <Tooltip title={value.metadata.text} placement={"left"}>
          <Button
            onClick={() => {
              props.setSelectedTool(props.selectedTool === value.key ? null : value.key as EditorTool);
            }}
            style={{
              borderRadius: 0,
              borderRight: props.selectedTool === value.key ? "4px solid " + theme.palette.primary.main : null,
              minWidth: 0,
              width: 48,
              height: 72
            }}>
            <div style={{display: "flex", alignItems: "center", justifyContent: "center"}}>
              <value.metadata.iconType/>
            </div>
          </Button>
        </Tooltip>)}
        <StyledSpan/>
      </div>
    </div>
  </>;
}

type WebPageEditorFragmentProps = DialogFragmentProps & {
  siteId: string,
  onBack: () => void,
}

type WebPageEditorFragmentState = DialogFragmentState & {
  site: Site,
  user?: User,
  practicePage?: DentwyzePage,
  webPage: WebPage,
  selectedTool?: EditorTool,
  sectionIndex: number,
  selectedImage?: string,
  hasUnsavedDraft: boolean,
  hasUnpublishedDraft: boolean,
  publishAttemptAt?: number,
}

export class WebPageEditorFragment extends DialogFragment<WebPageEditorFragmentProps, WebPageEditorFragmentState> {

  private readonly auth = getAuth();
  private readonly sites = new Sites(PracticeData.provider().practiceId);

  static nestedPaths(): PathComponent[] {
    return [
      {
        path: "manage",
        handle: {path: "manage", containerId: "editor"},
        render: pathProps => <SiteDomainsFragment path={pathProps} siteId={pathProps.params.site_id}/>,
      },
    ];
  }

  protected getContainerId(): string | null | undefined {
    return "editor";
  }

  componentDidUpdate(prevProps: Readonly<WebPageEditorFragmentProps>, prevState: Readonly<WebPageEditorFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.sectionIndex !== this.state.sectionIndex && this.state.selectedTool !== EditorTool.ATTRIBUTES) {
      this.setState({
        selectedTool: EditorTool.ATTRIBUTES,
      });
    }
  }

  protected async fetchOnMount(forceReload?: boolean): Promise<any> {
    const user = await UserCache.getInstance().getUser(this.auth.currentUser.uid);
    const practicePageId = BaseApp.CONTEXT.getTrait<AccountProfileContextTrait>().getPracticePageId();
    const practicePage = await Api.getInstance().fetchPageById(practicePageId);
    const site = await this.sites.getOrLoadItem(this.props.siteId || this.props.path.params.site_id);
    const draftVersion = site.draft();
    let webPage = draftVersion?.webPage;
    let hasUnpublishedDraft = webPage !== site.pages.published?.webPage;
    if (!webPage) {
      hasUnpublishedDraft = false;
      webPage = draftVersion.webPage = site.pages.published?.webPage || new WebPage();
      const profile = practicePage.practicePageProfile;
      const basicInfo = profile.basicInfo;
      webPage.sections.push(...[
        {
          name: "HeaderSection" as keyof SiteTemplatePageSections, data: HeaderSectionData.from({
            logo: basicInfo.logo,
            name: basicInfo.name,
            phone: basicInfo.phoneNumber,
            links: [
              Link.from({text: "Home", url: "/"}),
              Link.from({text: "Services", url: "/services"}),
              Link.from({text: "Team", url: "/team"}),
              Link.from({text: "Contact", url: "/contact"}),
            ],
          })
        },
        {
          name: "HeroSection" as keyof SiteTemplatePageSections, data: HeroSectionData.from({
            title: basicInfo.name,
            imageUrl: "/images/background.jpg",
            primaryLink: Link.from({
              text: "Schedule Appointment",
              url: "/",
            }),
          })
        },
        {
          name: "ServicesSection" as keyof SiteTemplatePageSections, data: ServicesSectionData.from({
            title: "For that Perfect Smile",
            subtitle: "From routine check-ups to complete smile makeovers, we have you covered.",
            displayMode: ServicesDisplayMode.COMPACT,
            services: [
              Service.from({
                type: ServiceType.GENERAL,
                title: "General Dentistry",
                description: "Routine cleanings, fillings, and check-ups to keep your teeth healthy and catch issues early.",
                link: Link.from({text: "Learn more", url: "/general-dentistry"}),
              }),
              Service.from({
                type: ServiceType.IMPLANTS,
                title: "Dental Implants",
                description: "Permanent, natural-looking replacements for missing teeth that restore your confidence and function.",
                link: Link.from({text: "Learn more", url: "/dental-implants"}),
              }),
              Service.from({
                type: ServiceType.ALIGNERS,
                title: "Orthodontics",
                description: "Traditional braces and clear aligners to straighten teeth at any age, discreetly and comfortably.",
                link: Link.from({text: "Learn more", url: "/orthodontics"}),
              }),
              Service.from({
                type: ServiceType.WHITENING,
                title: "Teeth Whitening",
                description: "Professional whitening treatments that remove stains and brighten your smile in a single visit.",
                link: Link.from({text: "Learn more", url: "/teeth-whitening"}),
              }),
              Service.from({
                type: ServiceType.VENEERS,
                title: "Veneers",
                description: "Ultra-thin porcelain shells custom-crafted to correct chips, gaps, and discolouration.",
                link: Link.from({text: "Learn more", url: "/veneers"}),
              }),
              Service.from({
                type: ServiceType.EMERGENCY,
                title: "Emergency Care",
                description: "Same-day appointments for toothaches, broken teeth, and other urgent dental needs.",
                link: Link.from({text: "Learn more", url: "/emergency-care"}),
              }),
            ],
          }),
        },
        {
          name: "PromoSection" as keyof SiteTemplatePageSections, data: PromoSectionData.from({
            eyebrow: "mySmile",
            title: "Try on your best smile",
            body: "See how a new smile could look on you before any treatment begins. Our digital smile preview lets you visualize your transformation in seconds.",
            imageUrl: "/images/mysmile.jpg",
            link: Link.from({text: "Try it now", url: `https://mysmile.dentwyze.in/?id=${practicePageId}`}),
            imagePosition: "right",
          })
        },
        {
          name: "StatsSection" as keyof SiteTemplatePageSections, data: StatsSectionData.from({
            stats: [
              Stat.from({value: "10,000+", label: "Happy Patients"}),
              Stat.from({value: "20+", label: "Years Experience"}),
              Stat.from({value: "4.9★", label: "Average Rating"}),
              Stat.from({value: "50+", label: "Specialist Dentists"}),
            ],
          })
        },
        {
          name: "GallerySection" as keyof SiteTemplatePageSections, data: GallerySectionData.from({
            title: "Our clinic",
            photos: profile.photos?.map(photo => GalleryPhoto.from({imageUrl: photo})),
          })
        },
        {
          name: "TeamSection" as keyof SiteTemplatePageSections, data: TeamSectionData.from({
            title: "Meet our doctors",
            subtitle: "A team of experienced specialists dedicated to your smile.",
            members: practicePage.getLinkedProviderPages()?.map(provider => {
              const profile = provider.providerPageProfile;
              const basicInfo = profile.basicInfo;
              return TeamMember.from({
                name: basicInfo.displayName(),
                role: profile.qualifications.degreesText(),
                bio: basicInfo.about,
                photoUrl: basicInfo.profilePhoto || "",
              });
            }),
          })
        },
        {
          name: "TestimonialsSection" as keyof SiteTemplatePageSections, data: TestimonialsSectionData.from({
            title: "What our patients say",
            testimonials: [
              Testimonial.from({
                quote: "The team here completely transformed my smile. I finally feel confident showing my teeth again.",
                authorName: "Sarah M.",
                authorTitle: "Patient since 2021",
                rating: 5,
              }),
              Testimonial.from({
                quote: "Professional, caring, and thorough. My dental implants look and feel completely natural.",
                authorName: "James T.",
                authorTitle: "Patient since 2022",
                rating: 5,
              }),
              Testimonial.from({
                quote: "I was nervous about my first visit but the staff made me feel totally at ease. Highly recommend.",
                authorName: "Priya K.",
                authorTitle: "Patient since 2023",
                rating: 5,
              }),
            ],
          })
        },
        {
          name: "AddressSection" as keyof SiteTemplatePageSections, data: AddressSectionData.from({
            title: "Visit us",
            addressLine1: profile.address.getAddress2Line()[0],
            addressLine2: profile.address.getAddress2Line()[1],
            phone: basicInfo.phoneNumber,
            email: basicInfo.email,
            openingHours: profile.calendar.schedule.entries?.map(entry => OpeningHours.from(entry.days.map(day => ({
              days: SCHEDULE_DAY_KTM.findMetadata(day).text,
              hours: [entry.session1, entry.session2].map(SessionText).filter(Boolean).join(", "),
            })))),
            mapEmbedUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3153.0!2d-122.4194!3d37.7749!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzfCsDQ2JzI5LjYiTiAxMjLCsDI1JzA5LjkiVw!5e0!3m2!1sen!2sus!4v1600000000000",
          })
        },
        {
          name: "FaqSection" as keyof SiteTemplatePageSections, data: FaqSectionData.from({
            title: "Frequently asked questions",
            subtitle: "Everything you need to know before your visit.",
            faqs: [
              FaqItem.from({
                question: "Do you accept dental insurance?",
                answer: "Yes, we accept most major dental insurance plans. Our team will verify your coverage before your appointment and walk you through any out-of-pocket costs.",
              }),
              FaqItem.from({
                question: "How often should I visit the dentist?",
                answer: "We recommend visiting every six months for a routine cleaning and check-up. Patients with certain conditions may benefit from more frequent visits.",
              }),
              FaqItem.from({
                question: "What should I bring to my first appointment?",
                answer: "Please bring a valid photo ID, your insurance card, and any previous dental records or X-rays if available. Arriving 10 minutes early helps us get started on time.",
              }),
              FaqItem.from({
                question: "Are your treatments suitable for children?",
                answer: "Absolutely. We offer a full range of pediatric dental services in a welcoming, child-friendly environment. We recommend bringing children in for their first visit by age one.",
              }),
            ],
          })
        },
        {
          name: "ContactSection" as keyof SiteTemplatePageSections, data: ContactSectionData.from({
            title: "Book an appointment",
            subtitle: "Fill in your details and we'll confirm your visit within 24 hours.",
          })
        },
        {
          name: "FooterSection" as keyof SiteTemplatePageSections, data: FooterSectionData.from({
            logo: basicInfo.logo,
            name: basicInfo.name,
            tagline: "Your smile, our expertise.",
            links: [
              Link.from({text: "Home", url: "/"}),
              Link.from({text: "Services", url: "/services"}),
              Link.from({text: "About", url: "/about"}),
              Link.from({text: "Contact", url: "/contact"}),
            ],
            copyrightText: `© ${new Date().getFullYear()} ${basicInfo.name}. All rights reserved.`,
          })
        },
      ]);
    }
    this.setState({
      user,
      practicePage,
      site: site,
      webPage: webPage,
      sectionIndex: 0,
      hasUnsavedDraft: false,
      hasUnpublishedDraft,
    });
  }

  private getDefaultSiteLink(): string {
    return SiteDomain.getDefaultSiteName(PracticeData.provider().practiceId, this.props.siteId);
  }

  private onMediaUploadClicked(webPage: WebPage): void {
    BaseApp.CONTEXT.showDialog(null, () => <MediaImageUploadFragment
      value={null}
      setValue={(value, file) => {
        webPage.media.images.push({
          id: md5_uuid(),
          name: file?.file.name,
          url: value,
        });
        this.saveDraft();
      }}/>);
  }

  renderContent(): React.ReactElement | null {
    if (!this.state.site) {
      return null;
    }
    const webPage = this.state.webPage;
    const template = TEMPLATES.find(template => template.name === this.state.site.templateName) || TEMPLATES[0];
    const sectionIndex = this.state.sectionIndex;
    return <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        flexBasis: 0,
        flexGrow: 1,
        background: lightGray,
        overflow: "hidden",
      }}>
      <StyledBoxRow style={{
        position: "relative",
        zIndex: 1,
        background: translucentWhite,
        width: "100%",
        alignItems: "center",
        padding: `0px ${PD_XLG}px`,
        height: 48,
        flexShrink: 0,
        borderBottom: DIVIDER,
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "absolute",
          left: 0,
          top: 0,
          width: "100%",
          height: "100%"
        }}>
          <div style={{width: 400}}>
            <StyledEditableTypography
              placeholder={"Web Page Title"}
              onTextInputConfirmed={(e, text) => {
                webPage.title = text;
                this.setState({
                  hasUnsavedDraft: false,
                });
                this.saveDraft();
              }}
              style={{width: "100%", textAlign: "center"}}
              variant={"h6"}>
              {webPage.title}
            </StyledEditableTypography>
          </div>
        </div>
        <Button
          startIcon={<ArrowBackIosNewOutlined/>}
          onClick={this.props.onBack}>Back</Button>
        <StyledSpan/>
        <Tooltip title={"Save"}>
          <IconButton
            disabled={!this.state.hasUnsavedDraft}
            onClick={() => this.saveDraft()}>
            <SaveOutlined/>
          </IconButton>
        </Tooltip>
        <Tooltip title={"Preview"}>
          <IconButton
            onClick={() => {
              BaseApp.CONTEXT.showDialog(
                {flags: DIALOG_FLAG_SHOW_DIALOG_FULLSCREEN | DIALOG_FLAG_SHOW_CLOSE},
                () => <Preview webPage={webPage} template={template}/>);
            }}>
            <PlayArrowOutlined/>
          </IconButton>
        </Tooltip>
        <PublishButton
          practiceId={PracticeData.provider().practiceId}
          siteId={this.props.siteId}
          publishAttemptAt={this.state.publishAttemptAt}
          disabled={!this.state.hasUnpublishedDraft}
          onClick={() => {
            BaseApp.CONTEXT.showConfirmDialog(
              "Are you sure you want to publish this website. Publishing will trigger a rebuild which can take up to an hour to finish.\n\n" +
              "You will not be able to publish again until the build finishes.",
              () => this.publish());
          }}
          color={"secondary"}
          startIcon={<LanguageOutlined/>}
          endIcon={<ArrowForwardIosOutlined/>}
          variant={"contained"}>
          Save & Publish
        </PublishButton>
        <IconButton onClick={(e) => {
          BaseApp.CONTEXT.showActionsListPopover(e.target as HTMLElement, {
            anchorOrigin: {
              horizontal: "center",
              vertical: "bottom"
            }
          }, [
            new Action("Manage Domains", () => {
              this.props.path.navigate("./manage");
            }, ManageAccountsOutlined),
            new Action("View Site", () => {
              window.open(this.getDefaultSiteLink(), "_blank");
            }, OpenInNewOutlined),
            new Action("Copy Link", () => {
              window.navigator.clipboard.writeText(this.getDefaultSiteLink())
                .then(() => BaseApp.CONTEXT.showToast("Copied link!"));
            }, LinkOutlined),
            new Action("Delete", () => {
              BaseApp.CONTEXT.showConfirmDialog(
                `Are you sure your want to delete your site \"${this.state.site.name}\"?\n\nYour site will be immediately deleted and your data will be lost. Visitors will no longer be able to access your site using the link.`,
                () => {
                },
                {maxWidth: DW_SM});
            }, DeleteOutlineOutlined)
              .makeDestructive(),
          ]);
        }}>
          <MoreVertOutlined/>
        </IconButton>
      </StyledBoxRow>
      <div style={{display: "flex", flexGrow: 1, height: "calc(100dvh-144px)", overflow: "hidden"}}>
        <Sidebar
          sidebarCollapsible={true}
          sidebarDefaultCollapsed={true}
          toolbar={<StyledBoxRow>
            <Button
              onClick={() => {
                this.onMediaUploadClicked(webPage);
              }}
              startIcon={<AddOutlined/>}>
              Upload
            </Button>
          </StyledBoxRow>}
          title={"Media"} position={SidebarPosition.LEFT}>
          <MediaView
            media={webPage.media}
            onUpload={() => this.onMediaUploadClicked(webPage)}
            selectedImage={this.state.selectedImage}
            setSelectedImage={id => this.setState({selectedImage: id})}
          />
        </Sidebar>
        <WebSectionContent
          webPage={webPage}
          template={template}
          selectedTool={this.state.selectedTool}
          setSelectedTool={tool => this.setState({selectedTool: tool})}
          sectionIndex={sectionIndex}
          setSectionIndex={sectionIndex => this.setState({sectionIndex})}
          selectedImage={this.state.selectedImage}
          setSelectedImage={id => this.setState({selectedImage: id})}/>
        {Boolean(this.state.selectedTool)
          ? <Sidebar
            title={EDITOR_TOOLS_KTM.findMetadata(this.state.selectedTool).text}
            position={SidebarPosition.RIGHT}>
            {this.state.selectedTool === EditorTool.TEMPLATE
              ? <SidebarContentTemplate
                templates={TEMPLATES}
                selectedTemplateName={this.state.site.templateName}
                setSelectedTemplateName={(templateName: string) => {
                  this.state.site.templateName = templateName;
                  this.setState({hasUnsavedDraft: true, hasUnpublishedDraft: true});
                  this.forceUpdate();
                }}/>
              : null}
            {this.state.selectedTool === EditorTool.ATTRIBUTES
              ? <SidebarContentAttributes
                sectionData={webPage.sections[sectionIndex].data}
                onSectionDataChanged={() => {
                  this.setState({hasUnsavedDraft: true, hasUnpublishedDraft: true});
                  this.forceUpdate();
                }}/>
              : null}
          </Sidebar>
          : null}
      </div>
    </div>;
  }

  private async saveDraft(): Promise<void> {
    const main = this;
    BaseApp.CONTEXT.showProgressDialog("Please wait...", null, async function () {
      await sleep(Math.random() * 1000);
      await main.state.site.save(main.sites);
      main.setState({
        hasUnsavedDraft: false,
      });
    });
  }

  private async publish(): Promise<void> {
    const main = this;
    BaseApp.CONTEXT.showProgressDialog("Please wait...", null, async function () {
      await main.state.site.publish(main.sites);
      main.setState({
        hasUnsavedDraft: false,
        hasUnpublishedDraft: false,
      });
      // Don't "await" on this as the network request lasts until the build finishes.
      try {
        VantageApi.getInstance().requestVantageSiteBuild(PracticeData.provider().practiceId, main.props.siteId);
        // Wait for the build to be queued on the server.
        await sleep(5000 + Math.random() * 3000);
        main.setState({
          publishAttemptAt: Date.now(),
        });
      } catch (e) {
        BaseApp.CONTEXT.showToast("Oops, something went wrong. Please try again later.");
      }
    });
  }
}